'use client'

import { Button, Image, Stack, Text } from '@mantine/core'
import { useErrorBoundaryFallbackProps } from '@suspensive/react'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'

import ExclamationCircle from '@/assets/icons/exclamation-circle.svg'
import illustrationError from '@/assets/images/illustration-error.png'
import { logger } from '@/configs'

import styles from './styles.module.css'

export function SectionErrorBoundaryFallback() {
  const { reset, error } = useErrorBoundaryFallbackProps()
  useEffect(() => {
    logger.error('SectionErrorBoundary caught error:', error)
  }, [error])
  const t = useTranslations('error')

  return (
    <Stack py="sm" gap="xs" align="center">
      <Image src={illustrationError.src} alt="Error" w={80} h={80} />
      <Text variant="body-emphasis" c="var(--content-primary)">
        {t('somethingWentWrong')}
      </Text>
      <Text variant="body-paragraph" c="var(--content-secondary)">
        {t('pleaseRefreshAgain')}
      </Text>
      <Button onClick={reset} variant="filled-primary">
        {t('refresh')}
      </Button>
    </Stack>
  )
}

export interface GenericErrorBoundaryFallbackProps {
  error: Error & { digest?: string }
  reset: () => void
}
export function GenericErrorBoundaryFallback({ error, reset }: GenericErrorBoundaryFallbackProps) {
  useEffect(() => {
    logger.error('GenericErrorBoundary caught error:', error)
  }, [error])

  const t = useTranslations('error')
  return (
    <Stack
      flex={1}
      pt={40}
      pb={{ base: 40, medium: 160 }}
      h="100%"
      justify="stretch"
      mx="-sm" /* Extend the horizontal padding of main container */
    >
      <Stack className={styles.genericContent}>
        <Text variant="story-text" c="var(--content-primary)">
          {t('somethingWentWrong')}
        </Text>
        <Stack gap="md">
          <Text variant="heading2" c="var(--content-primary)">
            {t('pleaseRefreshAgain')}
          </Text>
          <Button onClick={reset} variant="filled-primary" style={{ alignSelf: 'center' }}>
            {t('tryAgain')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}

export function OGSectionErrorBoundaryFallback() {
  const { reset, error } = useErrorBoundaryFallbackProps()
  useEffect(() => {
    logger.error('OG SectionErrorBoundary caught error:', error)
  }, [error])
  const t = useTranslations('error')

  return (
    <Stack py="sm" gap="xs" align="center">
      <ExclamationCircle width={32} height={32} />
      <Text variant="body-emphasis" c="var(--content-primary)">
        {t('somethingWentWrong')}
      </Text>
      <Text variant="body-paragraph" c="var(--content-secondary)">
        {t('pleaseRefreshAgain')}
      </Text>
      <Button onClick={reset} c="#E05800" bg="#37281E" w="320px">
        {t('refresh')}
      </Button>
    </Stack>
  )
}
